import type React from "react";
import { Paper, TextField, Button, Box, FormControl, Autocomplete } from "@mui/material";
import { Download as DownloadIcon } from "@mui/icons-material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { useTranslation } from "react-i18next";
import type { TableSearchProps } from "./Table";
import type translations from "../../translations/translations.json";
import { useState, useRef } from "react";

// Type for translation keys to ensure type safety
type TranslationKeys = keyof typeof translations.en;

/**
 * TableSearch Component
 * A reusable search component that renders a grid of search filters with clear functionality
 * and responsive layout.
 */
const TableSearch: React.FC<TableSearchProps> = ({
  searchFilters,
  onFilterChange,
  onClearField,
  onClearAll,
  onSearch,
  onKeyPress,
  filters,
  onDownload,
}) => {
  const { t } = useTranslation();
  const [focusedField, setFocusedField] = useState<string | null>(null);
  const [openStates, setOpenStates] = useState<Record<string, boolean>>({});
  const recentlyClosedRef = useRef<Record<string, number>>({});

  const handleOpenChange = (fieldId: string, isOpen: boolean) => {
    setOpenStates((prev) => ({
      ...prev,
      [fieldId]: isOpen,
    }));
    if (!isOpen) {
      // Track when we close a picker to prevent immediate reopening
      recentlyClosedRef.current[fieldId] = Date.now();
    }
  };

  const handleDatePickerFocus = (fieldId: string) => {
    // Only open if it's not already open and wasn't just closed (within 300ms)
    // Increased timeout to prevent reopening immediately after date selection
    const recentlyClosed = recentlyClosedRef.current[fieldId];
    const timeSinceClose = recentlyClosed ? Date.now() - recentlyClosed : Number.POSITIVE_INFINITY;

    if (!openStates[fieldId] && timeSinceClose > 300) {
      handleOpenChange(fieldId, true);
    }
  };

  return (
    <Paper
      sx={{
        p: 1.5,
        mb: 2,
        backgroundColor: "background.default",
        boxShadow: "none",
        border: "1px solid",
        borderColor: "divider",
        borderRadius: 2,
      }}
    >
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            sm: "repeat(6, 1fr)",
          },
          gap: 2,
          "& > *": {
            gridColumn: {
              xs: "1",
              sm: "span 3",
            },
          },
          // Only apply 2-column spans when we have an odd number of fields
          "&:has(> *:nth-of-type(odd):last-child) > *:nth-of-type(-n+3)": {
            gridColumn: {
              xs: "1",
              sm: "span 2",
            },
          },
        }}
      >
        {filters.map((filter) =>
          filter.type === "select" ? (
            <FormControl
              key={filter.id}
              fullWidth
              size="small"
              sx={{
                "& .MuiOutlinedInput-root": {
                  height: "36px",
                  backgroundColor: "background.paper",
                  "&:hover fieldset": {
                    borderColor: "primary.main",
                  },
                },
                "& .MuiInputLabel-root": {
                  fontSize: "0.875rem",
                },
              }}
            >
              <Autocomplete
                value={
                  filter.options?.find((opt) => opt.value === searchFilters[filter.id]) || null
                }
                onChange={(_, newValue) => {
                  onFilterChange(filter.id)({
                    target: { value: newValue?.value || "" },
                  } as React.ChangeEvent<HTMLInputElement>);
                  handleOpenChange(filter.id, false);
                }}
                options={[
                  { value: "", label: t("All" as TranslationKeys) },
                  ...(filter.options || []),
                ]}
                getOptionLabel={(option) => t(option.label as TranslationKeys)}
                renderInput={(params) => (
                  <TextField {...params} label={t(filter.label as TranslationKeys)} />
                )}
                size="small"
                open={openStates[filter.id] || false}
                onOpen={() => handleOpenChange(filter.id, true)}
                onClose={() => handleOpenChange(filter.id, false)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === "Escape") {
                    handleOpenChange(filter.id, false);
                  }
                }}
              />
            </FormControl>
          ) : filter.type === "text" ? (
            <FormControl
              key={filter.id}
              fullWidth
              size="small"
              sx={{
                "& .MuiOutlinedInput-root": {
                  height: "36px",
                  backgroundColor: "background.paper",
                  "&:hover fieldset": {
                    borderColor: "primary.main",
                  },
                },
                "& .MuiInputLabel-root": {
                  fontSize: "0.875rem",
                },
              }}
            >
              <Autocomplete
                freeSolo
                inputValue={
                  filter.formatter
                    ? filter.formatter(searchFilters[filter.id]?.toString() || "")
                    : searchFilters[filter.id]?.toString() || ""
                }
                onInputChange={(_, newValue) => {
                  // Use the unformatted value for search
                  const searchValue = newValue.replace(/\s/g, "").toUpperCase();
                  onFilterChange(filter.id)({
                    target: { value: searchValue },
                  } as React.ChangeEvent<HTMLInputElement>);
                }}
                options={filter.options || []}
                open={openStates[filter.id] || false}
                onOpen={() => handleOpenChange(filter.id, true)}
                onClose={() => handleOpenChange(filter.id, false)}
                onFocus={() => setFocusedField(filter.id)}
                onBlur={() => setFocusedField(null)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    name={filter.id}
                    label={t(filter.label as TranslationKeys)}
                    onKeyPress={onKeyPress}
                  />
                )}
                size="small"
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === "Escape") {
                    handleOpenChange(filter.id, false);
                  }
                }}
              />
            </FormControl>
          ) : filter.type === "date" ? (
            <FormControl key={filter.id} fullWidth size="small">
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                  label={t(filter.label as TranslationKeys)}
                  value={
                    searchFilters[filter.id]
                      ? (() => {
                          // Parse YYYY-MM-DD format in local timezone to avoid date shifting
                          const dateString = searchFilters[filter.id] as string;
                          const dateMatch = dateString.match(/^(\d{4})-(\d{2})-(\d{2})/);
                          if (dateMatch) {
                            const year = Number.parseInt(dateMatch[1], 10);
                            const month = Number.parseInt(dateMatch[2], 10) - 1; // Month is 0-indexed
                            const day = Number.parseInt(dateMatch[3], 10);
                            return new Date(year, month, day);
                          }
                          return new Date(dateString);
                        })()
                      : null
                  }
                  open={openStates[filter.id] || false}
                  onOpen={() => handleOpenChange(filter.id, true)}
                  onClose={() => handleOpenChange(filter.id, false)}
                  onChange={(newValue) => {
                    if (newValue) {
                      // Format date as YYYY-MM-DD without timezone conversion
                      const year = newValue.getFullYear();
                      const month = String(newValue.getMonth() + 1).padStart(2, "0");
                      const day = String(newValue.getDate()).padStart(2, "0");
                      const dateString = `${year}-${month}-${day}`;
                      onFilterChange(filter.id)({
                        target: { value: dateString },
                      } as React.ChangeEvent<HTMLInputElement>);
                      // Close the picker after date selection
                      // Use requestAnimationFrame to ensure it happens after the DatePicker processes the change
                      requestAnimationFrame(() => {
                        handleOpenChange(filter.id, false);
                      });
                    } else {
                      onFilterChange(filter.id)({
                        target: { value: "" },
                      } as React.ChangeEvent<HTMLInputElement>);
                    }
                  }}
                  slotProps={{
                    textField: {
                      size: "small",
                      onFocus: () => handleDatePickerFocus(filter.id),
                      sx: {
                        "& .MuiOutlinedInput-root": {
                          height: "36px",
                          backgroundColor: "background.paper",
                          "&:hover fieldset": {
                            borderColor: "primary.main",
                          },
                        },
                        "& .MuiInputLabel-root": {
                          fontSize: "0.875rem",
                        },
                      },
                    },
                  }}
                />
              </LocalizationProvider>
            </FormControl>
          ) : (
            <TextField
              key={filter.id}
              fullWidth
              label={t(filter.label as TranslationKeys)}
              value={searchFilters[filter.id] || ""}
              onChange={(e) => onFilterChange(filter.id)(e as React.ChangeEvent<HTMLInputElement>)}
              onKeyPress={onKeyPress}
              size="small"
              variant="outlined"
              sx={{
                "& .MuiOutlinedInput-root": {
                  height: "36px",
                  backgroundColor: "background.paper",
                  "&:hover fieldset": {
                    borderColor: "primary.main",
                  },
                },
                "& .MuiInputLabel-root": {
                  fontSize: "0.875rem",
                },
                "& .MuiOutlinedInput-input": {
                  py: 0.75,
                  fontSize: "0.875rem",
                },
              }}
            />
          ),
        )}
      </Box>
      <Box
        sx={{
          display: "flex",
          gap: 1,
          mt: 2,
        }}
      >
        <Button
          variant="outlined"
          type="button"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onSearch();
          }}
          fullWidth
          size="small"
          sx={{
            py: 0.5,
            fontSize: "0.875rem",
            textTransform: "none",
            borderColor: "divider",
            "&:hover": {
              borderColor: "primary.main",
              backgroundColor: "transparent",
            },
          }}
        >
          {t("Search" as TranslationKeys)}
        </Button>
        {onDownload && (
          <Button
            variant="outlined"
            type="button"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              // Create a deep copy of filters to ensure they're not affected by any state updates
              const filtersCopy = JSON.parse(JSON.stringify(searchFilters));
              onDownload(filtersCopy);
            }}
            size="small"
            startIcon={<DownloadIcon />}
            fullWidth
            sx={{
              py: 0.5,
              fontSize: "0.875rem",
              textTransform: "none",
              borderColor: "divider",
              "&:hover": {
                borderColor: "primary.main",
                backgroundColor: "transparent",
              },
            }}
          >
            {t("Download Report" as TranslationKeys)}
          </Button>
        )}
        {Object.values(searchFilters).some((value) => value) && (
          <Button
            variant="outlined"
            onClick={onClearAll}
            size="small"
            sx={{
              py: 0.5,
              px: 2,
              fontSize: "0.875rem",
              textTransform: "none",
              borderColor: "divider",
              color: "text.secondary",
              "&:hover": {
                borderColor: "error.main",
                backgroundColor: "transparent",
                color: "error.main",
              },
            }}
          >
            {t("Clear" as TranslationKeys)}
          </Button>
        )}
      </Box>
    </Paper>
  );
};

export default TableSearch;
